import { useEffect } from 'react';
import { useBlocker } from 'react-router-dom';

/**
 * @description Custom React hook that prompts the user with a window.confirm dialog
 * when they attempt to navigate away from a page with unsaved changes. Backed by
 * React Router v7's native useBlocker, which requires a data router (RouterProvider).
 *
 * @SADtrace SAD-003-4.1, SAD-003-4.2
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 * 06-09-2026 aleksandar dukleski: DLA-1505 reimplemented over native useBlocker (React Router v7)
 *
 * @param {string} message - The confirmation message to display to the user
 * @param {boolean} when - Condition that determines whether the prompt is active (default: true)
 *
 * @returns {void}
 */
export const usePrompt = (message: string, when = true): void => {
  const blocker = useBlocker(when);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      if (window.confirm(message)) blocker.proceed();
      else blocker.reset();
    }
    // Depend on the primitive blocker.state (not the blocker object, whose identity
    // changes every render) so window.confirm is shown once per blocked navigation.
  }, [blocker.state, message]); // eslint-disable-line react-hooks/exhaustive-deps

  // Release a stale "blocked" state if the guard turns off (e.g. form saved/submitted).
  useEffect(() => {
    // v8 ignore start -- WAIVER(DEFENSIVE): releases a stale blocked state when the guard flips off mid-navigation; that router interleaving cannot be held stable under unit rendering and is exercised by the E2E navigation flows
    if (!when && blocker.state === 'blocked') blocker.reset();
    // v8 ignore stop -- WAIVER(DEFENSIVE)
  }, [when, blocker.state]); // eslint-disable-line react-hooks/exhaustive-deps
};
